import type { ReasoningEffortOverride, ReasoningMode } from "@/stores/ai-explorer-store"

export type ReasoningPreset = {
  model_id: string
  /** `null` means no effort lock. For Auto it is the minimum effort instead (null = none). */
  effort: ReasoningEffortOverride | null
}

export type ReasoningPresets = Record<ReasoningMode, ReasoningPreset>

/** Client fallbacks — keep in sync with backend DEFAULT_REASONING_PRESETS. */
export const DEFAULT_REASONING_PRESETS: ReasoningPresets = {
  auto: { model_id: "auto", effort: null },
  fast: { model_id: "gpt-5.6-luna_eu", effort: "medium" },
  thinking: { model_id: "gpt-5.6-terra_eu", effort: "high" },
  big_brain: { model_id: "gpt-5.6-sol_eu", effort: "max" },
}

const PRESET_KEYS: ReasoningMode[] = ["auto", "fast", "thinking", "big_brain"]

const EFFORTS = new Set<ReasoningEffortOverride>([
  "none",
  "minimal",
  "low",
  "medium",
  "high",
  "xhigh",
  "max",
])

function isEffort(value: unknown): value is ReasoningEffortOverride {
  return typeof value === "string" && EFFORTS.has(value as ReasoningEffortOverride)
}

/**
 * Resolve a named preset's effort field. An explicit `null` is a deliberate
 * "no forced effort" signal — e.g. the backend omits it when this user's
 * effort access is restricted (#2667) or the preset's model isn't deployed.
 * Only a genuinely missing/malformed field falls back to the client default;
 * silently replacing an explicit null would resurrect a hardcoded effort the
 * user may not be allowed to run.
 */
function resolvePresetEffort(
  rawEffort: unknown,
  fallback: ReasoningEffortOverride | null
): ReasoningEffortOverride | null {
  if (rawEffort === null) return null
  return isEffort(rawEffort) ? rawEffort : fallback
}

/**
 * Merge partial admin/config presets with client defaults. Unknown keys and
 * invalid efforts are dropped field-wise.
 */
export function normalizeReasoningPresets(input: unknown): ReasoningPresets {
  const source = input && typeof input === "object" ? (input as Record<string, unknown>) : {}
  const result = { ...DEFAULT_REASONING_PRESETS }

  for (const key of PRESET_KEYS) {
    const raw = source[key]
    if (!raw || typeof raw !== "object") continue
    const preset = raw as Record<string, unknown>
    const modelId = typeof preset.model_id === "string" ? preset.model_id.trim() : ""
    if (key === "auto") {
      // Auto's effort is a server-enforced minimum, never a client lock (#3244):
      // resolveReasoningPresetLocks ignores it. Kept here so the admin dialog
      // can show and save it.
      result.auto = {
        model_id: modelId || "auto",
        effort: isEffort(preset.effort) ? preset.effort : null,
      }
      continue
    }
    result[key] = {
      model_id: modelId || DEFAULT_REASONING_PRESETS[key].model_id,
      effort: resolvePresetEffort(preset.effort, DEFAULT_REASONING_PRESETS[key].effort),
    }
  }

  return result
}

export type ApplyReasoningPresetResult = {
  modelOverride: string | null
  effortOverride: ReasoningEffortOverride | null
}

/**
 * Resolve store locks for a mode. If the preset model is not in the catalog,
 * keep effort lock only (modelOverride null → user's current / Auto model).
 */
export function resolveReasoningPresetLocks(
  mode: ReasoningMode,
  presets: ReasoningPresets,
  availableModelIds: ReadonlySet<string>
): ApplyReasoningPresetResult {
  if (mode === "auto") {
    return { modelOverride: null, effortOverride: null }
  }

  const preset = presets[mode]
  const modelAvailable =
    Boolean(preset.model_id) && preset.model_id !== "auto" && availableModelIds.has(preset.model_id)

  return {
    modelOverride: modelAvailable ? preset.model_id : null,
    effortOverride: preset.effort,
  }
}
